import React, { Fragment, useRef, useMemo } from "react";
import EditorHeader from "./EditorHeader";
import MobileEditorHeader from "./MobileEditorHeader";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { publishApplicationManifest } from "actions/application";
import { Formik, useFormikContext } from "formik";
import SectionNav from "./SectionNav";
import MobileSectionNav from "./MobileSectionNav";
import FeaturesContainer from "./FeaturesContainer";
import PageSettingsSection from "components/PageSettingsSection";
import PageWorkflowsSection from "components/PageWorkflowsSection";
import { updateUserEducation } from "actions/user";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import {
  getEditorInitialValues,
  SECTIONS
} from "components/FormerEditor/utils";
import { updateManifestParams, validateManifest } from "utils/manifest";
import { updateManifest } from "actions/manifest";
import { getAppManifestPublishParams } from "utils/application";
import { STATE_KEYS } from "utils/constants/state";
import PageBuilder from "components/FormerEditor/PageBuilder";
import MobilePageBuilder from "components/FormerEditor/MobilePageBuilder";
import { usePageBuilderContext, useViewport } from "utils/context";
import Analytics, {
  PUBLISH_APPLICATION,
  UPDATE_MANIFEST
} from "utils/analytics";
import { getManifestTrackingParams } from "utils/tracking/manifest";
import {
  useAppEntities,
  useApplicationTheme,
  useCommonEntitlementConfigData,
  useCouponPromotionCodes,
  useCtxReferences,
  useFeatureGroupFeatures,
  useManifest,
  useManifestShortLinks,
  useProductsPrices,
  useShippingRates,
  useTaxRates,
  useVariables
} from "utils/selectors";
import { useAdminContext } from "utils/state";
import { PAGE, SECTION } from "utils/constants/models";
import { useModelQueryId } from "utils/hooks";
import ProductsSection from "components/ApplicationSection/ProductsSection";
import { SETTINGS_TABS } from "utils/constants/settings";
import { NO_OP } from "utils/action";
import { NAV_BAR_HEIGHT } from "utils/constants/ui";
import { captureException } from "utils/error";

const EDITOR_PRODUCTS_SECTION_CLASSES = {
  index: {
    container: "w-100 bg-white overflow-y-scroll"
  },
  create: "w-100 bg-white pt4 overflow-y-scroll",
  edit: "w-100 bg-white overflow-y-scroll"
};

export const EDITOR_DESKTOP_SECTION_COMPONENT_MAP = {
  [SECTIONS.FEATURES]: () => {
    const { values } = useFormikContext();
    const pricingLayout = get(values, STATE_KEYS.MANIFEST.PRICING_LAYOUT);
    return (
      <div className="pt3 bg-white w-100">
        <FeaturesContainer layout={pricingLayout} />
      </div>
    );
  },
  [SECTIONS.SETTINGS]: () => (
    <div className="pt3 bg-white w-100">
      <PageSettingsSection sections={[SETTINGS_TABS.METADATA]} />
    </div>
  ),
  [SECTIONS.PRODUCTS]: () => (
    <ProductsSection customClasses={EDITOR_PRODUCTS_SECTION_CLASSES} />
  ),
  [SECTIONS.WORKFLOWS]: () => (
    <PageWorkflowsSection customClasses={EDITOR_PRODUCTS_SECTION_CLASSES} />
  ),
  [SECTIONS.PAGE_BUILDER]: PageBuilder
};

export const EDITOR_MOBILE_SECTION_COMPONENT_MAP = {
  [SECTIONS.FEATURES]: () => {
    const { values } = useFormikContext();
    const pricingLayout = get(values, STATE_KEYS.MANIFEST.PRICING_LAYOUT);
    return (
      <div className="pt3 bg-white w-100">
        <FeaturesContainer layout={pricingLayout} />
      </div>
    );
  },
  [SECTIONS.SETTINGS]: () => (
    <div className="pt3 bg-white w-100">
      <PageSettingsSection sections={[SETTINGS_TABS.METADATA]} />
    </div>
  ),
  [SECTIONS.PRODUCTS]: () => (
    <ProductsSection customClasses={EDITOR_PRODUCTS_SECTION_CLASSES} />
  ),
  [SECTIONS.WORKFLOWS]: () => (
    <PageWorkflowsSection customClasses={EDITOR_PRODUCTS_SECTION_CLASSES} />
  ),
  [SECTIONS.PAGE_BUILDER]: MobilePageBuilder
};

const FormerEditor = ({ actions }) => {
  const { isDesktop } = useViewport();
  const { resourceUUID, resource, application } = useCtxReferences();
  const editorFormRef = useRef(null);
  const builderCtx = usePageBuilderContext();
  const tracking = builderCtx ? builderCtx.tracking : {};

  const activeSection = useModelQueryId(SECTION);

  const uuid = useModelQueryId(PAGE);
  const manifest = useManifest(uuid, "uuid");

  /**
   * Select initialValues input variables
   */
  const variables = useVariables();
  const { products, prices } = useProductsPrices();
  const { features, featureGroups } = useFeatureGroupFeatures();
  const appEntities = useAppEntities();
  const admin = useAdminContext();
  const theme = useApplicationTheme();
  const shortLinks = useManifestShortLinks({ manifest });
  const taxRates = useTaxRates();
  const { coupons } = useCouponPromotionCodes();
  const shippingRates = useShippingRates();
  const entitlements = useCommonEntitlementConfigData();

  const valuesInput = {
    application,
    manifest,
    admin,
    theme,
    products,
    features,
    featureGroups,
    variables,
    prices,
    shortLinks,
    taxRates,
    shippingRates,
    coupons,
    appEntities,
    entitlements
  };
  const initialValues = useMemo(() => getEditorInitialValues(valuesInput), []);

  /**
   * Update manifest
   * - on complete sets manifest && theme
   */
  const updateManifest = (formValues, formikActions) => {
    const data = updateManifestParams({
      manifest: formValues.manifest,
      variables: formValues.variables,
      theme: formValues.theme,
      appEntities
    });

    const manifestTracking = getManifestTrackingParams({
      tracking,
      manifest: data.manifest,
      application: application
    });
    Analytics.track(UPDATE_MANIFEST, manifestTracking);

    actions.updateManifest(
      {
        resourceUUID,
        resource,
        uuid: formValues.manifest.uuid,
        data
      },
      (err) => {
        formikActions.setSubmitting(false);
        formikActions.setFieldValue(STATE_KEYS.EDITOR.PUBLISH_SHOW_MENU, false);
        if (err) {
          formikActions.setErrors(err);
        } else {
          formikActions.resetForm({ values: formValues });
        }
      }
    );
  };

  /**
   * Publish
   * - Updates manifest
   * - Publishes app
   *-  on complete sets app, deployment, manifest & theme
   */
  const publish = (publishParams, cb = NO_OP) => {
    const {
      values: { manifest, variables, theme },
      setErrors,
      setTouched,
      setFieldValue
    } = editorFormRef.current;

    if (publishParams && publishParams.route) {
      const data = getAppManifestPublishParams({
        publish: publishParams,
        manifest,
        variables,
        theme,
        application,
        appEntities
      });

      const manifestTracking = getManifestTrackingParams({
        tracking,
        manifest: data.manifest,
        application: application
      });
      Analytics.track(PUBLISH_APPLICATION, manifestTracking);

      actions.publishApplicationManifest(
        {
          resourceUUID,
          resource,
          uuid: application.uuid,
          data
        },
        (err, response) => {
          if (editorFormRef) {
            if (err) {
              setErrors(err);
            } else {
              setTouched({});
            }

            cb && cb(err, response);
          }
          setFieldValue(STATE_KEYS.EDITOR.PUBLISH_SHOW_MENU, false);
        }
      );
    } else {
      cb && cb();
      captureException(new Error("No publish route present"));
    }
  };

  // First two steps of publish but then redirect to subdomain.preview
  const preview = () => {
    console.log("**** PREVIEW");
  };

  return (
    <div
      className="formerEditor w-100 h-100 bg-content--secondary fixed z-2"
      style={{ top: NAV_BAR_HEIGHT }}
    >
      <Formik
        innerRef={editorFormRef}
        /**
         * Clone the initial values when passed to preserve the original reference
         * Pass the original reference through to the *Headers so that we can correctly Reset
         */
        initialValues={cloneDeep(initialValues)}
        onSubmit={updateManifest}
        validate={validateManifest({
          prices,
          products,
          coupons,
          taxRates,
          shippingRates
        })}
      >
        {() => {
          if (!activeSection) {
            return null;
          }
          const SectionComponent = isDesktop
            ? EDITOR_DESKTOP_SECTION_COMPONENT_MAP[activeSection]
            : EDITOR_MOBILE_SECTION_COMPONENT_MAP[activeSection];

          const desktopLayout = (
            <div className="flex-ns flex-column h-100 w-100 overflow-y-hidden-ns overflow-x-hidden-ns ">
              <EditorHeader
                preview={preview}
                publish={publish}
                initialValues={initialValues}
              />
              <div className="flex flex-row h-100 w-100">
                <SectionNav />
                <SectionComponent />
              </div>
            </div>
          );

          const mobileLayout = (
            <div className="flex flex-column w-100 overflow-y-hidden-ns overflow-x-hidden-ns ">
              <MobileEditorHeader
                preview={preview}
                publish={publish}
                initialValues={initialValues}
              />
              <MobileSectionNav />
              <div className="flex flex-column w-100 overflow-y-hidden-ns overflow-x-hidden-ns ">
                <SectionComponent />
              </div>
            </div>
          );

          return (
            <Fragment>{isDesktop ? desktopLayout : mobileLayout}</Fragment>
          );
        }}
      </Formik>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      updateManifest,
      publishApplicationManifest,
      updateUserEducation
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(FormerEditor);
